import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  Switch,
  TextField,
} from "@mui/material";
import { useEffect, useState } from "react";
import JalaliDatePicker from "../../../../components/shared/jalali-datepicker";
import {
  formatApiDate,
  validateGroupForm,
} from "../utils/commissionValidation";

const emptyForm = () => ({
  title: "",
  code: "",
  description: "",
  isActive: true,
  validFrom: new Date(),
  validTo: null,
});

const CommissionGroupFormDialog = ({ open, onClose, onSubmit, initialData, saving }) => {
  const [form, setForm] = useState(emptyForm());
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState(null);
  const isEdit = Boolean(initialData?.id);

  useEffect(() => {
    if (!open) return;
    setSubmitError(null);
    setErrors({});
    if (initialData) {
      setForm({
        title: initialData.title || "",
        code: initialData.code || "",
        description: initialData.description || "",
        isActive: initialData.isActive ?? true,
        validFrom: initialData.validFrom ? new Date(initialData.validFrom) : new Date(),
        validTo: initialData.validTo ? new Date(initialData.validTo) : null,
      });
    } else {
      setForm(emptyForm());
    }
  }, [open, initialData]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
    if (submitError) setSubmitError(null);
  };

  const handleSubmit = async () => {
    const validationErrors = validateGroupForm(form);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    const payload = {
      title: form.title.trim(),
      code: form.code.trim(),
      description: form.description?.trim() || null,
      isActive: form.isActive,
      validFrom: formatApiDate(form.validFrom),
      validTo: form.validTo ? formatApiDate(form.validTo) : null,
    };

    const result = await onSubmit(payload);
    if (result?.error) {
      setSubmitError(result.error);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? "ویرایش گروه پورسانتی" : "ایجاد گروه پورسانتی"}</DialogTitle>
      <DialogContent dividers>
        {submitError ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {submitError}
          </Alert>
        ) : null}

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              label="عنوان"
              value={form.title}
              onChange={(e) => handleChange("title", e.target.value)}
              fullWidth
              required
              error={Boolean(errors.title)}
              helperText={errors.title}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="کد"
              value={form.code}
              onChange={(e) => handleChange("code", e.target.value)}
              fullWidth
              required
              error={Boolean(errors.code)}
              helperText={errors.code}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={form.isActive}
                  onChange={(e) => handleChange("isActive", e.target.checked)}
                />
              }
              label="فعال"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="توضیحات"
              value={form.description}
              onChange={(e) => handleChange("description", e.target.value)}
              fullWidth
              multiline
              minRows={2}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <JalaliDatePicker
              label="تاریخ شروع"
              value={form.validFrom}
              onChange={(value) => handleChange("validFrom", value ? new Date(value) : null)}
              fullWidth
              error={Boolean(errors.validFrom)}
              helperText={errors.validFrom}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <JalaliDatePicker
              label="تاریخ پایان"
              value={form.validTo}
              onChange={(value) => handleChange("validTo", value ? new Date(value) : null)}
              fullWidth
              error={Boolean(errors.validTo)}
              helperText={errors.validTo}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          انصراف
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={saving}>
          {saving ? "در حال ذخیره..." : isEdit ? "ذخیره" : "ایجاد"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CommissionGroupFormDialog;
